import { createAction } from "@reduxjs/toolkit";

export const REFINE_HISTORY_LIMIT = 100;
export const refineHistoryActions = {
  undoRefine: createAction("refineHistory/undo"),
  redoRefine: createAction("refineHistory/redo"),
  endRefineEdit: createAction("refineHistory/endEdit"),
  setRefineHistoryEnabled: createAction("refineHistory/setEnabled"),
};

const emptyHistory = (enabled = false) => ({ enabled, past: [], future: [], group: null });
const snapshot = state => ({
  title: state.title,
  introduction: state.introduction,
  imageScale: state.imageScale,
});
const editTypes = new Set([
  "main/setTitle", "main/setIntroduction", "main/setImageScale",
]);
const groupedTypes = new Set(["main/setTitle", "main/setIntroduction", "main/setImageScale"]);

// Wrap the existing reducer so history never includes derived SVGs or URL state.
export const withRefineHistory = reducer => (state, action) => {
  const next = reducer(state, action);
  const history = state?.refineHistory || emptyHistory();
  if (action.type === refineHistoryActions.setRefineHistoryEnabled.type) {
    return { ...next, refineHistory: action.payload
      ? { ...history, enabled: true, group: null } : emptyHistory() };
  }
  if (action.type === "main/setRefineData" || action.type === "main/startNewChart" ||
      (action.type === "main/loadChartDocument" && next !== state) ||
      (action.type === "main/setMenuSelected" && ![2, 3].includes(action.payload))) {
    return { ...next, refineHistory: emptyHistory(history.enabled && action.type === "main/setRefineData") };
  }
  if (action.type === refineHistoryActions.endRefineEdit.type || action.type === "main/setMenuSelected") {
    return { ...next, refineHistory: { ...history, group: null } };
  }
  if (action.type === refineHistoryActions.undoRefine.type || action.type === refineHistoryActions.redoRefine.type) {
    const undo = action.type === refineHistoryActions.undoRefine.type;
    const source = undo ? history.past : history.future;
    if (!history.enabled || source.length === 0) return next;
    const current = snapshot(state);
    return { ...next, ...source[source.length - 1], refineHistory: {
      ...history, group: null,
      past: undo ? history.past.slice(0, -1) : [...history.past, current].slice(-REFINE_HISTORY_LIMIT),
      future: undo ? [...history.future, current].slice(-REFINE_HISTORY_LIMIT) : history.future.slice(0, -1),
    } };
  }
  if (history.enabled && editTypes.has(action.type)) {
    const before = snapshot(state);
    if (Object.keys(before).some(key => before[key] !== next[key])) {
      const group = groupedTypes.has(action.type) ? action.type : null;
      return { ...next, refineHistory: {
        ...history, group, future: [],
        past: group && history.group === group ? history.past
          : [...history.past, before].slice(-REFINE_HISTORY_LIMIT),
      } };
    }
  }
  return next.refineHistory ? next : { ...next, refineHistory: history };
};
